"use client";

import { useEffect } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useHreflang } from "@/i18n/useHreflang";
import HeaderStrip from "./components/HeaderStrip";
import HeroSection from "./components/HeroSection";
import StatsSection from "./components/StatsSection";
import WhatWeDo from "./components/WhatWeDo";
import WhyMaintenance from "./components/WhyMaintenance";
import MaintenancePlan from "./components/MaintenancePlan";
import ScopeSection from "./components/ScopeSection";
import NeighborhoodCoverage from "./components/NeighborhoodCoverage";
import BottomCta from "./components/BottomCta";
import FaqSection from "@/components/feature/FaqSection";
import ServiceAreasGrid from "@/components/feature/ServiceAreasGrid";
import RelatedServicesBlockService from "@/components/feature/RelatedServicesBlockService";
import LeadFormSection from "@/components/feature/LeadFormSection";
import AboutServiceSection from "@/components/feature/AboutServiceSection";
import { injectMetaTags } from "@/lib/seoHelpers";

export default function WindowCleaningMaintenancePage() {
  const { t, currentLang } = useLanguage();
  useHreflang();

  const maintAboutImg = currentLang === "es"
    ? "https://storage.readdy-site.link/project_files/76915537-ee3f-4564-9a23-41bd98b5df27/b85a9e78-3e8d-4832-81f9-8b2a681fb303_Window-Cleaning--Maintenance-Miami.jpg?v=6ae0d61422bf6fa6e68708b75b97ddfb"
    : "https://storage.readdy-site.link/project_files/76915537-ee3f-4564-9a23-41bd98b5df27/b85a9e78-3e8d-4832-81f9-8b2a681fb303_Window-Cleaning--Maintenance-Miami.jpg?v=6ae0d61422bf6fa6e68708b75b97ddfb";

  const maintenanceFaqs = [
    {
      question: t("maint.faq1Q") as string,
      answer: t("maint.faq1A") as string,
    },
    {
      question: t("maint.faq2Q") as string,
      answer: t("maint.faq2A") as string,
    },
    {
      question: t("maint.faq3Q") as string,
      answer: t("maint.faq3A") as string,
    },
  ];

  useEffect(() => {
    document.title = currentLang === "es"
      ? "Mantenimiento Anual de Ventanas Miami — $129 Tarifa Plana"
      : "Annual Window Maintenance Miami — $129 Flat-Rate";

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute(
      "content",
      currentLang === "es"
        ? "Programa de mantenimiento de ventanas Miami. Puesta a punto anual de $129 extiende vida útil por años. Limpieza de rieles, lubricación de herrajes, inspección de burletes. Especialista costero."
        : "Miami window maintenance program. $129 annual tune-up extends window life by years. Track cleaning, hardware lubrication, weatherstripping inspection. Coastal-specialized."
    );

    const cleanupSeo = injectMetaTags({
      title: currentLang === "es"
        ? "Mantenimiento Anual de Ventanas Miami — $129 Tarifa Plana"
        : "Annual Window Maintenance Miami — $129 Flat-Rate",
      description: currentLang === "es"
        ? "Programa de mantenimiento de ventanas Miami. Puesta a punto anual de $129 extiende vida útil por años. Limpieza de rieles, lubricación de herrajes, inspección de burletes. Especialista costero."
        : "Miami window maintenance program. $129 annual tune-up extends window life by years. Track cleaning, hardware lubrication, weatherstripping inspection. Coastal-specialized.",
      url: "https://impact-window-repair-miami.com/window-cleaning-maintenance/",
      image: "https://impact-window-repair-miami.com/og/og-maintenance.jpg",
      imageAlt: currentLang === "es" ? "Mantenimiento anual de ventanas Miami" : "Annual window maintenance Miami",
      breadcrumbs: [
        { name: currentLang === "es" ? "Inicio" : "Home", item: "https://impact-window-repair-miami.com/" },
        { name: currentLang === "es" ? "Limpieza y Mantenimiento" : "Window Cleaning & Maintenance", item: "https://impact-window-repair-miami.com/window-cleaning-maintenance/" }
      ],
      type: "website",
    });

    const schema = {
      "@context": "https://schema.org",
      "@type": "Service",
      name: currentLang === "es" ? "Limpieza y Mantenimiento de Ventanas" : "Window Cleaning and Maintenance",
      provider: {
        "@type": "LocalBusiness",
        name: "Windows and Doors Repair LLC",
        telephone: "+17863774452",
        email: "contact@impact-window-repair-miami.com",
        url: "https://impact-window-repair-miami.com",
        address: {
          "@type": "PostalAddress",
          streetAddress: "848 Brickell Ave, PH-5",
          addressLocality: "Miami",
          addressRegion: "FL",
          postalCode: "33131",
          addressCountry: "US",
        },
        geo: {
          "@type": "GeoCoordinates",
          "latitude": 25.7617,
          "longitude": -80.1918
        },
        openingHoursSpecification: [{
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
          "opens": "07:00",
          "closes": "21:00"
        }],
        priceRange: "$$",
        paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
        currenciesAccepted: "USD",
        areaServed: [
          {"@type": "City", "name": "Miami"},
          {"@type": "AdministrativeArea", "name": "Miami-Dade County"},
          "Brickell", "Aventura", "Miami Beach", "Coral Gables", "Coconut Grove", "Doral", "Kendall", "Pinecrest", "Bay Harbor Islands", "Key Biscayne", "South Miami", "North Miami Beach"
        ],
        knowsLanguage: ["en", "es"],
        knowsAbout: ["Window Hardware Repair", "Sliding Door Repair", "Window Glass Replacement", "Weatherstripping", "Window Maintenance", "Emergency Board-Up"],
        slogan: "Honest Miami Window Repair. Same-Day. 30-Day Guarantee."
      },
      serviceType: currentLang === "es" ? "Limpieza y Mantenimiento de Ventanas" : "Window Cleaning and Maintenance",
      description: currentLang === "es"
        ? "Mantenimiento anual de ventanas y puertas incluyendo puesta a punto, limpieza de rieles, lubricación, limpieza de ventanas e inspecciones preventivas en Miami-Dade County."
        : "Annual window and door maintenance including tune-ups, track cleaning, lubrication, window cleaning, and preventive inspections across Miami-Dade County.",
      inLanguage: currentLang === "es" ? "es-US" : "en-US",
    };

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      inLanguage: currentLang === "es" ? "es-US" : "en-US",
      mainEntity: maintenanceFaqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    };

    const script = document.createElement("script");
    script.setAttribute("type", "application/ld+json");
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    const faqScript = document.createElement("script");
    faqScript.setAttribute("type", "application/ld+json");
    faqScript.id = "faq-schema";
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    return () => {
      cleanupSeo();
      document.head.removeChild(script);
      document.head.removeChild(faqScript);
    };
  }, [currentLang]);

  return (
    <div className="font-roboto">
      <HeaderStrip />
      <HeroSection />
      <LeadFormSection defaultService="maintenance" />
      <AboutServiceSection
        badge={t("maint.eyebrow") as string}
        imageSrc={maintAboutImg}
        imageAlt="Miami maintenance specialist performing window track cleaning and hardware lubrication"
        heading={t("maint.aboutHeading") as string}
        lead={t("maint.intro") as string}
        bullets={[
          t("maint.aboutBullet1") as string,
          t("maint.aboutBullet2") as string,
          t("maint.aboutBullet3") as string,
          t("maint.aboutBullet4") as string,
          t("maint.aboutBullet5") as string,
        ]}
        closing={t("maint.aboutClosing") as string}
      />
      <StatsSection />
      <WhatWeDo />
      <WhyMaintenance />
      <MaintenancePlan />
      <ScopeSection />
      <NeighborhoodCoverage />
      <FaqSection heading={t("maint.faqH2") as string} items={maintenanceFaqs} />
      <ServiceAreasGrid serviceName="Window Cleaning & Maintenance" serviceSlug="window-cleaning-maintenance" />
      <RelatedServicesBlockService
        links={[
          { label: t("maint.related1") as string, path: "/sliding-door-repair/" },
          { label: t("maint.related2") as string, path: "/window-hardware-repair/" },
          { label: t("maint.related3") as string, path: "/weatherstripping-sealing/" },
        ]}
      />
      <BottomCta />
    </div>
  );
}